import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, CheckCircle2, Users, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaveSetting = () => {
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [message, setMessage] = useState({ type: '', text: '' });
  const [leaveTypes, setLeaveTypes] = useState([]);

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/leave-types`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data);
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const handleAllocateLeave = async () => {
    if (!year) {
      setMessage({ type: 'error', text: 'Please select a year' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-management/allocate-annual-leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ year: parseInt(year) })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Successfully allocated leave to ${data.total_staff} staff members. Created ${data.allocations_created} allocation records.`
        });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to allocate leave' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leave Allocation Settings</h1>
        <p className="text-gray-600 mt-1">Allocate annual leave quotas to staff members</p>
      </div>

      {/* Leave Types Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Configured Leave Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {leaveTypes.map((type) => (
              <div
                key={type.id}
                className="p-4 rounded-lg border-2"
                style={{ borderColor: type.color }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">{type.name}</h3>
                    <p className="text-xs text-gray-500">{type.short_code}</p>
                  </div>
                  <div
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: type.color + '20',
                      color: type.color
                    }}
                  >
                    {type.is_paid ? 'Paid' : 'Unpaid'}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Annual Quota:</span>
                  <span className="font-bold" style={{ color: type.color }}>
                    {type.annual_quota > 0 ? `${type.annual_quota} days` : 'Unlimited'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Allocation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Allocate Annual Leave
          </CardTitle>
        </CardHeader>
        <CardContent>
          {message.text && (
            <div
              className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              {message.text}
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Important Information
              </h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>This will allocate leave quotas to all active staff members</li>
                <li>Staff members who already have allocations for this year will be skipped</li>
                <li>Leave types will be allocated based on their configured annual quotas</li>
                <li>Allocation is typically done at the beginning of each year</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Year <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-gray-400" />
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  min="2020"
                  max="2050"
                  className="max-w-xs"
                  placeholder="Enter year (e.g., 2025)"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleAllocateLeave}
                disabled={loading || !year}
                className="gap-2"
                size="lg"
              >
                <Users className="h-4 w-4" />
                {loading ? 'Allocating...' : `Allocate Leave for ${year}`}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="mt-6 bg-gray-50">
        <CardHeader>
          <CardTitle className="text-sm">How Leave Allocation Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-700">
            <div>
              <strong>1. Annual Setup:</strong> Allocate leave at the start of each year for all staff.
            </div>
            <div>
              <strong>2. Automatic Balance Tracking:</strong> The system tracks allocated, used, pending, and current balance for each leave type.
            </div>
            <div>
              <strong>3. Leave Application:</strong> Staff can apply for leave through the "Leave Apply" page. Applications are validated against their available balance.
            </div>
            <div>
              <strong>4. Approval Process:</strong> Admins can approve/reject leave applications from the "Manage Leave" page.
            </div>
            <div>
              <strong>5. Attendance Integration:</strong> Approved leaves are automatically marked in the attendance system.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveSetting;